import { useQuery } from "@tanstack/react-query";
import { addMinutes, formatISO, subMinutes } from "date-fns";

import {
  MetricsNodeStatusCounts,
  MetricsNodeStatusCountsResponse,
} from "src/api/types";
import { DecisionEnvironment } from "src/clients/history_v3";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { getJobRunFlags } from "src/jobs/types";
import { aggregateStatusCounts } from "src/performance/nodeStatusUtils";
import { metricsQueryKeys, useNodeStatusCounts } from "src/performance/queries";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useCurrentJobRun, useNodeRunState } from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";

export const useMetricsQueryData = (
  jobRunId: string,
): MetricsNodeStatusCountsResponse | undefined => {
  const { data } = useQuery<MetricsNodeStatusCountsResponse>({
    queryKey: metricsQueryKeys.nodeStatusCounts(jobRunId),
    queryFn: () => ({ status_counts: {} }),
    enabled: false,
  });
  return data;
};

export const useJobRunStatusCountsPolling = () => {
  const { workspace, flow } = useAuthoringContext();
  const { nodesArray } = useGraphStore();
  const jobRun = useCurrentJobRun();
  const outputNodeId =
    nodesArray.find((n) => n.type === NODE_TYPE.OUTPUT_NODE)?.id ?? "";

  const { isJobRunning, isJobTerminated } = getJobRunFlags(jobRun?.status.type);

  useNodeStatusCounts({
    baseUrl: workspace.base_url ?? "",
    jobRunId: jobRun?.id ?? "",
    flowId: flow.id,
    environment: DecisionEnvironment.TEST,
    fromStartTime: formatISO(
      subMinutes(new Date(jobRun?.created_at ?? new Date()), 60),
    ),
    untilStartTime: formatISO(
      addMinutes(new Date(jobRun?.updated_at ?? new Date()), 60),
    ),
    isRunning: isJobRunning,
    expectedTotal: jobRun?.total_rows_count ?? 0,
    terminatedAt: isJobTerminated ? jobRun?.updated_at : undefined,
    outputNodeId,
  });
};

export const useJobRunMetrics = () => {
  const { nodesArray } = useGraphStore();
  const jobRun = useCurrentJobRun();
  const outputNodeId =
    nodesArray.find((n) => n.type === NODE_TYPE.OUTPUT_NODE)?.id ?? "";

  const data = useMetricsQueryData(jobRun?.id ?? "");

  if (!data || !jobRun) return undefined;

  return { data, outputNodeId };
};

export const useJobRunCountsForNode = (
  nodeId: string,
): MetricsNodeStatusCounts | undefined => {
  const nodeRunState = useNodeRunState(nodeId);
  const isJobRun = nodeRunState?.type === "job-run";
  const metrics = useJobRunMetrics();

  if (!isJobRun || !metrics) return undefined;
  return metrics.data.status_counts[nodeId];
};

// Verifies that all rows are written to decision history.
// Not an indicator of job run completion. Helps to determine if a node was skipped.
export const useIsJobRunMetricsWritten = () => {
  const metrics = useJobRunMetrics();
  const jobRun = useCurrentJobRun();

  if (metrics) {
    const aggregatedCounts = aggregateStatusCounts(
      metrics.data.status_counts,
      metrics.outputNodeId,
    );
    const total =
      aggregatedCounts.totalSuccess +
      aggregatedCounts.totalFailed +
      aggregatedCounts.totalIgnored +
      aggregatedCounts.totalOther +
      aggregatedCounts.totalPending +
      aggregatedCounts.totalOutputMismatch;

    return total >= (jobRun?.total_rows_count ?? 0);
  }

  return false;
};

export const useOutputNodeJobRunCounts = ():
  | {
      success: number;
      failed: number;
      ignored: number;
      total: number;
      output_mismatch: number;
    }
  | undefined => {
  const metrics = useJobRunMetrics();
  if (!metrics) return undefined;

  const {
    totalSuccess,
    totalFailed,
    totalIgnored,
    totalOther,
    totalPending,
    totalOutputMismatch,
  } = aggregateStatusCounts(metrics.data.status_counts, metrics.outputNodeId);

  return {
    success: totalSuccess,
    failed: totalFailed,
    ignored: totalIgnored,
    output_mismatch: totalOutputMismatch,
    total:
      totalSuccess +
      totalFailed +
      totalIgnored +
      totalOther +
      totalPending +
      totalOutputMismatch,
  };
};
